import type { FC } from 'react';
import { useCallback, useState } from 'react';
import {
  type FieldValues,
  useFormContext,
  useFormState,
  useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { HelperText, Text, TextInput } from 'react-native-paper';

import { MultilineEditorModal } from '@/features/profile-setting/components/organization-edit/MultilineEditorModal';
import type { OrganizationEditStyleSheet } from '@/features/profile-setting/components/organization-edit/organizationEditFormStyles';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

type Props = {
  name: string;
  sectionTitle: string;
  placeholder: string;
  editorTitle: string;
  editorPlaceholder: string;
  maxLength: number;
  isPending: boolean;
  styles: OrganizationEditStyleSheet;
};

export const MultilineTeaserField: FC<Props> = ({
  name,
  sectionTitle,
  placeholder,
  editorTitle,
  editorPlaceholder,
  maxLength,
  isPending,
  styles
}) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const { control, setValue } = useFormContext<FieldValues>();
  const fieldName = name;
  const value =
    (useWatch({ control, name: fieldName }) as string | undefined) ?? '';
  const { errors } = useFormState({ control });
  let fieldError: unknown = errors;
  for (const key of name.split('.')) {
    fieldError = (fieldError as Record<string, unknown> | undefined)?.[key];
  }
  const errorMessage = (fieldError as { message?: string } | undefined)
    ?.message;

  const [editorVisible, setEditorVisible] = useState(false);
  const openEditor = useCallback(() => setEditorVisible(true), []);
  const closeEditor = useCallback(() => setEditorVisible(false), []);
  const apply = useCallback(
    (next: string) => {
      setValue(fieldName, next, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, fieldName]
  );

  return (
    <View style={styles.section}>
      <Text variant="titleMedium" style={styles.sectionTitle}>
        {sectionTitle}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={sectionTitle}
        accessibilityHint={t('accessibility.fieldHint')}
        onPress={() => {
          if (!isPending) {
            openEditor();
          }
        }}
        disabled={isPending}
      >
        <View style={{ pointerEvents: 'none' }}>
          <TextInput
            accessibilityLabel={sectionTitle}
            accessibilityHint={t('accessibility.fieldHint')}
            mode="outlined"
            placeholder={placeholder}
            value={value}
            editable={false}
            multiline
            numberOfLines={4}
            disabled={isPending}
            error={!!errorMessage}
            style={styles.descriptionInput}
          />
        </View>
      </Pressable>
      {errorMessage && (
        <HelperText type="error" visible>
          {errorMessage}
        </HelperText>
      )}
      <MultilineEditorModal
        visible={editorVisible}
        value={value}
        title={editorTitle}
        placeholder={editorPlaceholder}
        maxLength={maxLength}
        onConfirm={apply}
        onRequestClose={closeEditor}
      />
    </View>
  );
};
